import { useEventAdminManager } from "@di-devs/event-feature-plugin/client";
import {
	MemberMembershipStatusFilter,
	MemberRegistrationStatusFilter,
} from "@di-devs/member-feature-plugin/client";
import { Col, DatePicker, Divider, Form, Row, Select } from "antd";
import dayjs from "dayjs";
import type React from "react";
import { useMemo } from "react";

const EventAudienceFilters: React.FC = () => {
	const { events, isLoading } = useEventAdminManager();
	const form = Form.useFormInstance();
	const occurrenceStartAt = Form.useWatch("occurrenceStartAt", form);
	const occurrenceEndAt = Form.useWatch("occurrenceEndAt", form);

	const participationOptions = useMemo(
		() => [
			{ value: "participate", label: "参加" },
			{ value: "reserve", label: "予約" },
			{ value: "cancel", label: "キャンセル" },
		],
		[],
	);

	return (
		<>
			<Divider>イベントで検索</Divider>
			<Row gutter={[16, 16]}>
				<Col xs={24}>
					<Form.Item name="eventOccasionId">
						<Select
							allowClear
							showSearch
							placeholder="イベントを選択"
							loading={isLoading}
							optionFilterProp="label"
							options={events.map((o: any) => ({
								value: o.eventOccasionId,
								label: o.title,
							}))}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider>参加状況で検索</Divider>
			<Row gutter={[16, 16]}>
				<Col xs={24}>
					<Form.Item name="participationStatus">
						<Select
							allowClear
							placeholder="参加状況を選択"
							options={participationOptions}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider>イベント日時で検索</Divider>
			<Row gutter={[16, 16]}>
				<Col xs={24}>
					<Form.Item>
						<div className="flex gap-2">
							<Form.Item
								name="occurrenceStartAt"
								noStyle
								getValueFromEvent={(val) =>
									val
										? dayjs(val).second(0).format("YYYY-MM-DD HH:mm:ss")
										: undefined
								}
								getValueProps={(val) => ({
									value: val ? dayjs(val) : undefined,
								})}
							>
								<DatePicker
									placeholder="開始"
									className="flex-1"
									showTime={{ format: "HH:mm" }}
									format="YYYY-MM-DD HH:mm"
									disabledDate={(current) => {
										if (!occurrenceEndAt) return false;
										return (
											current && current > dayjs(occurrenceEndAt).endOf("day")
										);
									}}
									disabledTime={(current) => {
										if (!occurrenceEndAt || !current) return {};
										const endDay = dayjs(occurrenceEndAt);
										if (current.isSame(endDay, "day")) {
											return {
												disabledHours: () =>
													Array.from({ length: 24 }, (_, i) => i).filter(
														(h) => h > endDay.hour(),
													),
												disabledMinutes: (selectedHour) => {
													if (selectedHour === endDay.hour()) {
														return Array.from(
															{ length: 60 },
															(_, i) => i,
														).filter((m) => m > endDay.minute());
													}
													return [];
												},
											};
										}
										return {};
									}}
								/>
							</Form.Item>
							<span className="flex items-center">〜</span>
							<Form.Item
								name="occurrenceEndAt"
								noStyle
								getValueFromEvent={(val) =>
									val
										? dayjs(val).second(59).format("YYYY-MM-DD HH:mm:ss")
										: undefined
								}
								getValueProps={(val) => ({
									value: val ? dayjs(val) : undefined,
								})}
							>
								<DatePicker
									placeholder="終了"
									className="flex-1"
									showTime={{ format: "HH:mm" }}
									format="YYYY-MM-DD HH:mm"
									disabledDate={(current) => {
										if (!occurrenceStartAt) return false;
										return (
											current &&
											current < dayjs(occurrenceStartAt).startOf("day")
										);
									}}
									disabledTime={(current) => {
										if (!occurrenceStartAt || !current) return {};
										const startDay = dayjs(occurrenceStartAt);
										if (current.isSame(startDay, "day")) {
											return {
												disabledHours: () =>
													Array.from({ length: 24 }, (_, i) => i).filter(
														(h) => h < startDay.hour(),
													),
												disabledMinutes: (selectedHour) => {
													if (selectedHour === startDay.hour()) {
														return Array.from(
															{ length: 60 },
															(_, i) => i,
														).filter((m) => m < startDay.minute());
													}
													return [];
												},
											};
										}
										return {};
									}}
								/>
							</Form.Item>
						</div>
					</Form.Item>
				</Col>
			</Row>
			<Divider>会員登録状態で検索</Divider>
			<MemberRegistrationStatusFilter />
			<Divider>会員ステータスで検索</Divider>
			<MemberMembershipStatusFilter />
		</>
	);
};

export default EventAudienceFilters;
